import { recordInterest } from "../utils/session-interest";
import { ResizedImg } from "./img";
import { googleEvent } from "../utils/dom";
import { Swiper, SwiperSlide } from "swiper/react";
import type { SwiperRef } from "swiper/react";
import ListingStatus from "./listing-status";
import PercentOffBadge from "./percent-off-badge";
import { Thumbs, Zoom, A11y, Navigation, Pagination } from "swiper/modules";
import { useEffect, useState, useRef } from "react";
import { getAnalytics } from "../utils/analytics";
import { useNotifications } from "../contexts";
import { useAdminListingMenu } from "../hooks/useAdminListingMenu";
import { useListingThumbApi } from "./useListingThumbApi";
import AdminContextMenu from "./admin-context-menu";
import type SwiperType from "swiper";

interface SwiperImage {
  url?: string;
  thumbUrl?: string;
  '160x160Url'?: string;
}

interface SwiperProduct {
  id?: string;
  title?: string;
  price?: string | number;
  categoryId?: string;
  categoryGroupId?: number;
  status?: string | number;
  dataSourceId?: number;
  /** Feeds the session interest vector when the buyer steps through photos. */
  tags?: string[];
  /** Set while the listing is featured; only the admin menu reads it. */
  featuredDate?: string | Date | null;
}

// Swiper component refs expose .swiper on the DOM element
interface SwiperElementRef {
  swiper?: SwiperType;
}

const SwiperImages = ({ images, bigPhotoSwiperRef, thumbSwiperRef, product, tag: _tag, percentOff, sourcePage = 'product_modal' }: {
  images: SwiperImage[];
  bigPhotoSwiperRef: React.RefObject<SwiperElementRef | null>;
  thumbSwiperRef: React.RefObject<SwiperElementRef | null>;
  product: SwiperProduct;
  tag?: string;
  percentOff?: number;
  sourcePage?: 'product_modal' | 'product_page' | 'store_page';
}) => {
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperType | null>(null);

  // Admin right-click menu on the main photo -- the same feature / partner /
  // archive actions a listing tile offers, on the page where an admin is
  // already looking at the listing. `useAdminListingMenu` refuses to open for
  // anyone who is not an admin, so a logged-in shopper's right-click gets the
  // browser's own menu and nothing of ours is ever rendered for them.
  const bigPhotoRef = useRef<HTMLDivElement>(null);
  const { isAdmin, menu: adminMenu, openMenu, closeMenu } = useAdminListingMenu(bigPhotoRef);
  const { createNotification } = useNotifications();
  const adminListingId = Number(product?.id);
  const canAdminister = isAdmin && Number.isSafeInteger(adminListingId) && adminListingId > 0;
  const {
    isFeatureLoading,
    isPartnerLoading,
    isArchiveLoading,
    handleFeature,
    handleFeatureForPartner,
    handleArchive,
    isNsfwLoading,
    handleNsfw,
    loadNsfwState,
    nsfwFor,
  } = useListingThumbApi({
    component: 'SwiperImages',
    // The listing stays on screen here, unlike a tile that leaves its grid, so
    // say what happened.
    onSuccess: (action, detail) => {
      const msg =
        action === 'partner'
          ? `Featured for ${detail?.partnerName}`
          : action === 'archive'
            ? 'Listing archived'
            : action === 'nsfw'
              ? detail?.nsfw === false
                ? 'Not NSFW: back in rows and browse'
                : 'Marked NSFW: out of rows and browse, still in search'
            // The admin menu only ever features (Feature, Refeature).
            : 'Listing featured';
      createNotification({ type: 'success', msg, dismissed: false });
    },
  });

  // Which product has already earned its "stepped through the photos"
  // interest credit. Once per product, not per swipe: ten swipes on one
  // listing is one deliberate act, not ten.
  const photosSignalledFor = useRef<string | undefined>(undefined);

  // Filter out invalid images
  const displayImages = images && images.length > 0
    ? images.filter(img => img?.url && !img.url.includes("default"))
    : [];

  const hidden = displayImages?.length <= 1;
  const nav = displayImages?.length > 3;

  const status = product?.status;

  useEffect(() => {
    // Reset swiper position without triggering skeleton animation
    if (bigPhotoSwiperRef?.current?.swiper) bigPhotoSwiperRef.current.swiper.slideTo(0);
    if (thumbSwiperRef?.current?.swiper) thumbSwiperRef.current.swiper.slideTo(0);
  }, [images, bigPhotoSwiperRef, thumbSwiperRef]);

  return (
    <>
      <div className="product-images same-color-border">
        <div
          className="product-images-cont"
          ref={bigPhotoRef}
          onContextMenu={canAdminister ? openMenu : undefined}
        >
  				{percentOff ? (
            <PercentOffBadge percentOff={percentOff} />
          ) : (
            <ListingStatus status={status} />
          )}
          <Swiper
            // onSwiper={(swiper) => (bigPhotoSwiperRef.current = swiper))
            ref={bigPhotoSwiperRef as unknown as React.Ref<SwiperRef>}
            className="big-photos"
            modules={[Thumbs, A11y, Pagination, Zoom]}
            edgeSwipeDetection={true}
            edgeSwipeThreshold={40}
            preventClicks={true}
            pagination={true}
            zoom={true}
            thumbs={{ swiper: thumbsSwiper }}
            spaceBetween={50}
            slidesPerView={1}
            watchSlidesProgress
            onSlideChange={(swiper) => {
              // Track with legacy Google Analytics
              googleEvent("swipe_product_photo");

              // Session interest: stepping through photos is deliberate, and
              // rarer than a hover. Guarded on activeIndex > 0 because the
              // reset effect above calls slideTo(0) when a new product loads,
              // which fires this handler without the buyer doing anything.
              if (swiper.activeIndex > 0 && product?.id && photosSignalledFor.current !== product.id) {
                photosSignalledFor.current = product.id;
                recordInterest({ signal: 'photos', tags: product.tags, categoryGroupId: product.categoryGroupId });
              }

              // Track with new analytics system
              const analytics = getAnalytics();
              if (analytics && product?.id) {
                analytics.photoSlider({
                  listingId: parseInt(product.id),
                  listingTitle: product.title,
                  listingPrice: product.price ? parseFloat(String(product.price)) : undefined,
                  categoryId: product.categoryId ? parseInt(product.categoryId) : undefined,
                  categoryGroupId: product.categoryGroupId,
                  slideIndex: swiper.activeIndex,
                  totalPhotos: displayImages.length,
                  interactionType: 'swipe',
                  sourcePage,
                });
              }
            }}
            breakpoints={{
              821: {
                pagination: false,
              },
            }}
          >
            {/* Keyed by position, not URL: a feed listing can carry the same
                photo URL twice, and duplicate keys inside a Swiper let React
                and Swiper disagree about which node to remove. */}
            {displayImages?.map((i, idx) => (
              <SwiperSlide zoom={true} className="big-photo" key={`photo-${idx}-${i.url}`}>
                <ResizedImg
                  src={i.url}
                  // The slide renders at 640 CSS px; 1280 is only worth
                  // downloading on a 2x screen, which srcSet decides.
                  size={640}
                  retinaSize={1280}
                  fade
                  dataSourceId={product?.dataSourceId}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>
      {canAdminister && (
        <AdminContextMenu
          isVisible={adminMenu.visible}
          position={{ x: adminMenu.x, y: adminMenu.y }}
          listingRect={adminMenu.listingRect}
          listingId={adminListingId}
          isFeatured={!!product?.featuredDate}
          onFeature={handleFeature}
          onFeatureForPartner={handleFeatureForPartner}
          onArchive={handleArchive}
          onNsfw={handleNsfw}
          onLoadNsfw={loadNsfwState}
          isNsfw={nsfwFor(adminListingId)}
          onClose={closeMenu}
          isFeatureLoading={isFeatureLoading}
          isPartnerLoading={isPartnerLoading}
          isArchiveLoading={isArchiveLoading}
          isNsfwLoading={isNsfwLoading}
        />
      )}
      <div className={`product-image-thumbs ${hidden ? "hidden" : ""}`} data-testid="product-thumbs-container">
        <div className="product-image-thumbs-cont">
          <Swiper
            className="product-thumbs"
            ref={thumbSwiperRef as unknown as React.Ref<SwiperRef>}
            watchSlidesProgress
            direction="horizontal"
            threshold={10}
            freeMode={false}
            modules={[Thumbs, A11y, Navigation]}
            navigation={nav ? {
              prevEl: '.product-thumbs-prev',
              nextEl: '.product-thumbs-next',
            } : false}
            onSwiper={setThumbsSwiper}
            slidesPerView={"auto"}
            // 0, not a gap value: with slidesPerView "auto" Swiper measures each
            // slide's COMPUTED margin into slideSize (it clears its own inline
            // margin first), then adds spaceBetween on top -- so any non-zero value
            // here is counted twice in virtualSize while the DOM only ever renders
            // the inline one. The gap is owned by .product-thumb-photo's margin in
            // styles/pages/product.scss, where four thumbs and three 12px gaps
            // fill the column exactly. A gap counted twice overran the row and
            // the next arrow never locked.
            spaceBetween={0}
            preventInteractionOnTransition={false}
          >
            {displayImages?.map((i, idx) => (
              <SwiperSlide
                className="product-thumb-photo same-color-border"
                key={`thumb-${idx}-${i.url}`}
              >
                <div className="image-cont">
                  {/* Through ResizedImg rather than a hand-rolled <img>: it owns
                      the whole format chain -- AVIF where the browser takes it,
                      webp when there is no AVIF sibling, then .jpg for listings
                      predating the resizer. This used to carry its own webp->jpg
                      fallback, which meant the thumbnails alone never got AVIF. */}
                  <ResizedImg
                    src={i.url}
                    // Renders at up to ~151px (see .product-thumb-photo); 160 is the
                    // smallest variant above that, 320 covers 2x screens.
                    size={160}
                    retinaSize={320}
                    alt={`Listing Photo Thumb #${idx + 1}`}
                    className="swiper-lazy"
                    dataSourceId={product?.dataSourceId}
                  />
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
          {nav && (
            <>
              <div
                className="product-thumbs-prev swiper-button-prev"
                data-testid="product-thumbs-prev"
              />
              <div
                className="product-thumbs-next swiper-button-next"
                data-testid="product-thumbs-next"
              />
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default SwiperImages;
